import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import Initial from 'react-initial';
import * as Showdown from 'showdown';
import { useDropzone } from 'react-dropzone';
import 'react-mde/lib/styles/css/react-mde-all.css';

import { ContentState, EditorState } from 'draft-js';
import { convertFromHTML } from 'draft-convert';
import RichTextEditor from 'draft-js-plugins-editor';
import { stateToHTML } from 'draft-js-export-html';
import createDndFileUploadPlugin from '@draft-js-plugins/drag-n-drop-upload';
import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import createLinkPlugin from '@draft-js-plugins/anchor';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  HeadlineOneButton,
  HeadlineTwoButton,
  UnorderedListButton,
  BlockquoteButton,
} from '@draft-js-plugins/buttons';
import 'draft-js/dist/Draft.css';

import { Button, LoadingSpinner } from '../_shared';
import { uploadToS3 } from '../../utils/helper';
import { ToolbarContainer } from './PostForm.styled';

const toolbarConfig = {
  // Optionally specify the groups to display (displayed in the order listed).
  display: [
    'INLINE_STYLE_BUTTONS',
    'BLOCK_TYPE_BUTTONS',
    'LINK_BUTTONS',
    'HISTORY_BUTTONS',
  ],
  INLINE_STYLE_BUTTONS: [
    { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' },
  ],
};

const linkPlugin = createLinkPlugin();
const dndFileUploadPlugin = createDndFileUploadPlugin();
const staticToolbarPlugin = createToolbarPlugin();
const { Toolbar } = staticToolbarPlugin;
const plugins = [staticToolbarPlugin, dndFileUploadPlugin, linkPlugin];

const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});

const IconButton = styled.button.attrs({
  className: 'text-xl p-1 mr-3',
})``;
const Container = styled.div.attrs({
  className: 'flex items-start',
})`
  .avatar {
    min-width: 30px;

    img {
      width: 30px;
      height: 30px;
    }

    & + div {
      flex-grow: 1;
    }
  }

  @media only screen and (max-width: 639px) {
    .avatar {
      min-width: 35px;

      img {
        width: 35px;
        height: 35px;
      }
    }
  }

  .placeholder {
    padding: 6px 12px;
    width: 100%;

    &::placeholder {
      ${tw`text-gray-800 text-xs leading-normal`}
    }
  }

  .editor {
    border: 1px solid #333;

    .header {
      background: white;
      padding: 8px 16px;
      box-shadow: 0px 3px 6px #0000001a;
    }

    .react-mde {
      border: none;
      margin: 0;
      margin-bottom: 10px;
      background: white;
    }

    .mde-text,
    .mde-preview {
      border-bottom-left-radius: 15px;
      border-bottom-right-radius: 15px;
      padding: 16px;
    }

    .mde-header {
      font-size: 13px;
      background: white;
      border: none;
      border-top-left-radius: 15px;
      border-top-right-radius: 15px;

      .mde-tabs {
        width: 100%;
        justify-content: center;

        button {
          padding: 4px 20px;
          margin-right: 20px;
          border: none;
          &.selected {
            border-bottom: 3px solid;
          }
        }
      }

      .mde-header-group {
        &.hidden {
          display: none;
        }

        @media only screen and (max-width: 639px) {
          display: none;
        }
      }
    }
  }

  @media only screen and (max-width: 639px) {
    .editor {
      ${tw`rounded-none bg-gray-800 w-screen h-screen`}
      position: fixed;
      top: 0;
      left: 0;
      z-index: 1001;

      .title {
        margin-bottom: 0;
      }
    }
  }

  .complete-btn {
    ${tw`bg-primary text-white`}
    border-radius: 5px;
  }
`;
const Editor = styled.div.attrs({
  className: 'p-4 pt-0 sm:p-4',
})`
  box-shadow: 0px 15px 34px rgba(98, 120, 140, 0.1);

  .toolbar div {
    background: transparent !important;
    margin-right: 8px !important;

    button {
      height: 24px;
      padding: 0px;
    }
  }

  a {
    color: blue;
    text-decoration: underline;
  }

  .video-script {
    width: 100%;
    background: #eee;
  }
`;

const Comment = styled.input.attrs({
  className: 'bg-white focus:outline-none text-black w-full',
})`
  box-shadow: 0px 15px 34px rgba(98, 120, 140, 0.1);
  padding: 6px 15px;
  border-radius: 5px;

  &::placeholder {
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    color: rgba(98, 120 140, 0.3);
  }
`;

const NewComment = ({
  user,
  busy,
  editing = false,
  content = '',
  onSubmit,
  onCancel,
}) => {
  const { name, role, profileImg } = user;
  const [value, setValue] = useState(content || 'Post Reply');
  const [selectedTab, setSelectedTab] = useState('write');
  const [showEditor, setShowEditor] = useState(editing);
  const [inputing, setInputing] = useState(editing);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [hasVideo, setHasVideo] = useState(false);
  const [video, setVideo] = useState('');

  useEffect(() => {
    const blocksFromHTML = convertFromHTML(content);
    // const state = ContentState.createFromBlockArray(
    //   blocksFromHTML.contentBlocks,
    //   blocksFromHTML.entityMap
    // );
    setEditorState(EditorState.createWithContent(blocksFromHTML));
  }, [content]);

  useEffect(() => setShowEditor(editing), [editing]);

  const handleEditorChange = (es) => {
    setEditorState(es);
  };
  const handleHideEditor = () => setShowEditor(false);
  const handleShowEditor = () => {
    setValue('Post Reply');
    setShowEditor(true);
  };

  const handleSubmit = async () => {
    const filesResult = [];

    if (uploadFiles.length) {
      setUploadingStatus(
        uploadFiles.reduce(
          (prev, cur) => ({ ...prev, [cur.name]: 'reading' }),
          {}
        )
      );

      const xlsxExt = 'vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const res = await Promise.all(
        uploadFiles.map((file) =>
          uploadToS3(file, '', file.type.indexOf(xlsxExt) > -1 ? '.xlsx' : '')
        )
      );

      const result = {};
      for (let i = 0; i < uploadFiles.length; i += 1) {
        if (res[i].status === 204) {
          filesResult.push(
            res[i].key
              .split('/')[1]
              .replace(new RegExp(String.raw`${xlsxExt}`, 'g'), 'xlsx')
          );
          result[uploadFiles[i].name] = 'success';
        } else {
          result[uploadFiles[i].name] = 'error';
        }
      }

      setUploadingStatus(result);
    }

    onSubmit({
      value: stateToHTML(editorState.getCurrentContent()),
      video: role === 'admin' ? video : null,
      attachments: filesResult,
    });
    setShowEditor(false);
  };
  const handleClickEditor = () => {
    if (value === 'Post Reply') {
      setValue('');
    }
  };

  const handleDropFiles = (files) => setUploadFiles(files);
  const handleRemoveFile = (name) => () => {
    setUploadFiles(uploadFiles.filter((file) => file.name !== name));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDropFiles,
  });

  useEffect(() => {
    const editor = document.getElementsByClassName('comment-editor')[0];
    if (editor) {
      editor.addEventListener('focus', handleClickEditor);
    }

    return () => {
      editor && editor.removeEventListener('focus', handleClickEditor);
    };
  }, [showEditor, value]);

  useEffect(() => {
    if (!busy && !editing) {
      setShowEditor(false);
    }
  }, [busy, editing]);

  return (
    <Container>
      <div className="avatar rounded-full overflow-hidden mr-3">
        <Initial
          width={30}
          height={30}
          fontSize={10}
          circle={true}
          color="rgba(24, 28, 52)"
          name={name}
        />
      </div>
      {showEditor ? (
        <div className="editor flex flex-col">
          <div className="hidden sm:flex header items-center justify-between">
            <IconButton onClick={handleHideEditor}>{'<'}</IconButton>
            <div className="title text-base font-bold leading-base flex-grow">
              New Comment
            </div>
            <Button caption="Submit" busy={busy} onClick={handleSubmit} />
          </div>
          <Editor>
            <div className="mt-4" />
            {inputing ? (
              <>
                <RichTextEditor
                  className="main-editor max-h-150 min-h-32 overflow-auto"
                  editorState={editorState}
                  toolbarConfig={toolbarConfig}
                  spellCheck={true}
                  plugins={plugins}
                  onChange={handleEditorChange}
                />
                {hasVideo && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold">Video Script</p>
                    <textarea
                      placeholder="Add video script here..."
                      rows="2"
                      className="video-script p-2 text-sm"
                      value={video}
                      onChange={(e) => setVideo(e.target.value)}
                    />
                  </div>
                )}
              </>
            ) : (
              <input
                placeholder="Type reply here"
                onClick={() => setInputing(true)}
              />
            )}
            <div className="mb-4" />
            <div
              {...getRootProps({
                className:
                  'cursor-pointer text-center p-2 border-2 border-dashed border-gray-800 text-gray-800 hover:text-gray-200 hover:border-gray-200 w-full mb-2',
              })}
            >
              <input {...getInputProps()} />
              <p>Drag and drop files here or click to select file</p>
            </div>
            {uploadFiles.length > 0 && (
              <div className="flex flex-col items-start mb-12">
                {uploadFiles.map((file) => (
                  <div
                    className="file-button flex items-center bg-gray-800 p-1 mt-1"
                    key={file.name}
                  >
                    {file.name}
                    <div className="status ml-4">
                      {uploadingStatus[file.name] === 'reading' ? (
                        <LoadingSpinner size="16px" />
                      ) : uploadingStatus[file.name] === 'error' ? (
                        <span className="text-red text-tinay">Error!</span>
                      ) : (
                        <span
                          role="button"
                          onClick={handleRemoveFile(file.name)}
                        >
                          X
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ToolbarContainer>
              <Toolbar>
                {
                  // may be use React.Fragment instead of div to improve perfomance after React 16
                  (externalProps) => (
                    <div className="toolbar flex items-start h-6 flex-grow">
                      <BoldButton {...externalProps} />
                      <ItalicButton {...externalProps} />
                      <UnderlineButton {...externalProps} />
                      <UnorderedListButton {...externalProps} />
                      <HeadlineOneButton {...externalProps} />
                      <HeadlineTwoButton {...externalProps} />
                      <BlockquoteButton {...externalProps} />
                      <linkPlugin.LinkButton {...externalProps} />
                      {role === 'admin' && (
                        <button
                          type="button"
                          className="text-sm p-2 add-video-btn"
                          onClick={() => setHasVideo((v) => !v)}
                        >
                          {hasVideo ? 'Remove Video' : 'Attach Video'}
                        </button>
                      )}
                    </div>
                  )
                }
              </Toolbar>
              <div className="sm:hidden flex justify-end mr-2">
                <Button
                  className="font-semibold px-4 py-2 text-base font-bold complete-btn uppercase mr-2"
                  caption="Submit"
                  type="misc"
                  busy={busy}
                  onClick={handleSubmit}
                />
                <Button
                  className="font-semibold px-4 py-2 text-base font-bold complete-btn uppercase"
                  caption="Cancel"
                  type="misc"
                  busy={busy}
                  onClick={
                    typeof onCancel === 'function' ? onCancel : handleHideEditor
                  }
                />
              </div>
            </ToolbarContainer>
          </Editor>
        </div>
      ) : (
        <Comment placeholder="Post Reply..." onClick={handleShowEditor} />
      )}
    </Container>
  );
};

export default React.memo(NewComment);
